/**
 * Produces a stable string hash from the filter state + technician/subcontractor selections.
 * Only includes fields that actually affect the API query.
 */
function buildFilterHash(filters, selectedTechnicianIds, selectedSubcontractorIds) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    return JSON.stringify({
        type: (_a = filters === null || filters === void 0 ? void 0 : filters.values) === null || _a === void 0 ? void 0 : _a.type,
        building: (_b = filters === null || filters === void 0 ? void 0 : filters.values) === null || _b === void 0 ? void 0 : _b.building,
        asset: (_c = filters === null || filters === void 0 ? void 0 : filters.values) === null || _c === void 0 ? void 0 : _c.asset,
        technicianTeam: (_d = filters === null || filters === void 0 ? void 0 : filters.values) === null || _d === void 0 ? void 0 : _d.technicianTeam,
        territory: (_e = filters === null || filters === void 0 ? void 0 : filters.values) === null || _e === void 0 ? void 0 : _e.territory,
        tags: (_f = filters === null || filters === void 0 ? void 0 : filters.values) === null || _f === void 0 ? void 0 : _f.tags,
        buildingTags: (_g = filters === null || filters === void 0 ? void 0 : filters.values) === null || _g === void 0 ? void 0 : _g.buildingTags,
        accountTags: (_h = filters === null || filters === void 0 ? void 0 : filters.values) === null || _h === void 0 ? void 0 : _h.accountTags,
        workOrderTags: (_j = filters === null || filters === void 0 ? void 0 : filters.values) === null || _j === void 0 ? void 0 : _j.workOrderTags,
        inspectionType: (_k = filters === null || filters === void 0 ? void 0 : filters.values) === null || _k === void 0 ? void 0 : _k.inspectionType,
        inspectionStatusKeys: (_l = filters === null || filters === void 0 ? void 0 : filters.values) === null || _l === void 0 ? void 0 : _l.inspectionStatusKeys,
        visitStatusKeys: (_m = filters === null || filters === void 0 ? void 0 : filters.values) === null || _m === void 0 ? void 0 : _m.visitStatusKeys,
        // Legacy Kendo filters (non–hasService header) — must bust cache when these change (IP-13256)
        inspectionStatus: (_o = filters === null || filters === void 0 ? void 0 : filters.values) === null || _o === void 0 ? void 0 : _o.inspectionStatus,
        visitStatus: (_p = filters === null || filters === void 0 ? void 0 : filters.values) === null || _p === void 0 ? void 0 : _p.visitStatus,
        account: (_q = filters === null || filters === void 0 ? void 0 : filters.values) === null || _q === void 0 ? void 0 : _q.account,
        additionalTerritory: (_r = filters === null || filters === void 0 ? void 0 : filters.values) === null || _r === void 0 ? void 0 : _r.additionalTerritory,
        techIds: [...selectedTechnicianIds].sort(),
        subIds: [...selectedSubcontractorIds].sort()
    });
}
function buildTimeOffHash(filters, selectedTechnicianIds) {
    var _a, _b;
    return JSON.stringify({
        technicianTeam: (_a = filters === null || filters === void 0 ? void 0 : filters.values) === null || _a === void 0 ? void 0 : _a.technicianTeam,
        territory: (_b = filters === null || filters === void 0 ? void 0 : filters.values) === null || _b === void 0 ? void 0 : _b.territory,
        techIds: [...selectedTechnicianIds].sort()
    });
}
function buildGenericEventsHash(filters, selectedTechnicianIds) {
    var _a, _b;
    return JSON.stringify({
        type: (_a = filters === null || filters === void 0 ? void 0 : filters.values) === null || _a === void 0 ? void 0 : _a.type,
        technicianTeam: (_b = filters === null || filters === void 0 ? void 0 : filters.values) === null || _b === void 0 ? void 0 : _b.technicianTeam,
        techIds: [...selectedTechnicianIds].sort()
    });
}
/**
 * Centralized query key factory for the Calendar feature.
 *
 * All React Query hooks and mutations import from here to ensure consistent
 * keys across queries and cache invalidation.
 */
export const calendarKeys = {
    /** Base key — use to invalidate the entire calendar cache */
    all: ['calendar'],
    /**
     * Key for calendar visits queries.
     * Includes range, view type, and a hash of all active filters + assignee selections.
     */
    visits: (rangeStart, rangeEnd, view, filters, selectedTechnicianIds, selectedSubcontractorIds) => [
        ...calendarKeys.all,
        'visits',
        rangeStart,
        rangeEnd,
        view,
        buildFilterHash(filters, selectedTechnicianIds, selectedSubcontractorIds)
    ],
    /**
     * Key for time-offs queries.
     * Filtered by technician IDs, team and territory only.
     */
    timeOffs: (rangeStart, rangeEnd, filters, selectedTechnicianIds) => [...calendarKeys.all, 'timeOffs', rangeStart, rangeEnd, buildTimeOffHash(filters, selectedTechnicianIds)],
    /** Key for holidays — only depends on date range */
    holidays: (rangeStart, rangeEnd) => [...calendarKeys.all, 'holidays', rangeStart, rangeEnd],
    /**
     * Key for generic events queries.
     * Filtered by type, team, and technician selections.
     */
    genericEvents: (rangeStart, rangeEnd, filters, selectedTechnicianIds) => [
        ...calendarKeys.all,
        'genericEvents',
        rangeStart,
        rangeEnd,
        buildGenericEventsHash(filters, selectedTechnicianIds)
    ],
    /** Key for on-demand full visit detail (used by modal) */
    visitDetail: (visitId) => [...calendarKeys.all, 'visitDetail', visitId]
};
